import React from "react";

import { IgTypography } from "@intelligems/ig-theme/IgTypography";
import Grid from "@mui/material/Grid";
import { AiMessageAccordion } from "@src/features/ai/components/WorkflowEditor/execution/components/AIMessageAccordion";
import { ReasoningMarkdown } from "./ReasoningMarkdown";
import { getReasoningTexts, shouldShowReasoningPart } from "../part-processor";
import {
  FlattenedPart,
  MessageFlags,
} from "@src/features/ai/components/WorkflowEditor/execution/types";

type ReasoningGroupProps = {
  parts: FlattenedPart[];
} & MessageFlags;

const BodyComponent = (props: Omit<ReasoningGroupProps, "headerText">) => {
  const { parts } = props;

  const contents = parts
    .map((part) => {
      if (part.part.type !== "reasoning") {
        return "";
      }

      if (props.debug) {
        return part.part.text;
      }

      if (!props.showReasoningMessages && part.nodeType === "reasoning") {
        return getReasoningTexts(part.part, false).headerText;
      }

      if (part.nodeType === "condition" && !props.showConditionNodes) {
        return "";
      }

      return part.part.text;
    })
    .filter(Boolean);

  return (
    <Grid container direction={"column"}>
      {contents.map((part, i) => (
        <Grid gap={2} key={`reasoning-part-${i}`}>
          <ReasoningMarkdown
            text={part}
            disableResponseWordByWordLoading={
              props.disableResponseWordByWordLoading
            }
          />
        </Grid>
      ))}
    </Grid>
  );
};

export const ReasoningGroup = (props: ReasoningGroupProps) => {
  let parts = props.parts;
  if (
    !props.debug &&
    (!props.showReasoningMessages || !props.showConditionNodes)
  ) {
    parts = parts.filter((part) => shouldShowReasoningPart(part, props));
  }

  if (parts.length === 0) {
    return null;
  }

  if (props.showExpandedReasoningMessages) {
    return (
      <Grid container direction={"column"} sx={{ mb: 2 }}>
        <IgTypography
          color={"text.secondary"}
          fontWeight={600}
          variant={"body2"}
        >
          Prior {parts.length} Steps...
        </IgTypography>
        <BodyComponent {...props} parts={parts} />
      </Grid>
    );
  }

  return (
    <AiMessageAccordion
      headerComponent={
        <ReasoningMarkdown
          text={`__Prior ${parts.length} Steps...__`}
          disableResponseWordByWordLoading={
            props.disableResponseWordByWordLoading
          }
        />
      }
      isExpandable={true}
      bodyComponent={<BodyComponent {...props} parts={parts} />}
    />
  );
};
